import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

import type { CreativeKind } from '../models/analysis.types'
import type { CreativeAiTab } from '../models/creativeAi.constants'

import type { ReportFilter, ReportRow } from '@/features/reports'

export type CreativeViewMode = 'cards' | 'table'

export interface CreativeViewPreference {
  kind: CreativeKind
  mode: CreativeViewMode
  columns: string[]
}

const DEFAULT_PREFERENCE: CreativeViewPreference = { kind: 'image', mode: 'cards', columns: [] }

export const useCreativeAiViewStore = defineStore(
  'creativeAiView',
  () => {
    const activeTab = ref<CreativeAiTab>('all-creatives')
    const preferences = ref<Record<string, CreativeViewPreference>>({})
    const sidebarCreative = shallowRef<ReportRow | null>(null)
    const allCreativesModal = ref<{ open: boolean; presetFilters: ReportFilter[] }>({
      open: false,
      presetFilters: [],
    })

    function preference(scopeKey: string): CreativeViewPreference {
      return preferences.value[scopeKey] ?? { ...DEFAULT_PREFERENCE }
    }

    function patchPreference(scopeKey: string, patch: Partial<CreativeViewPreference>): void {
      preferences.value = {
        ...preferences.value,
        [scopeKey]: { ...preference(scopeKey), ...patch },
      }
    }

    function setTab(tab: CreativeAiTab): void {
      activeTab.value = tab
    }

    function openCreative(row: ReportRow): void {
      sidebarCreative.value = row
    }

    function closeCreative(): void {
      sidebarCreative.value = null
    }

    function openAllCreatives(filters: ReportFilter[] = []): void {
      allCreativesModal.value = {
        open: true,
        presetFilters: filters.map((filter) => ({ ...filter })),
      }
    }

    function closeAllCreatives(): void {
      allCreativesModal.value = { open: false, presetFilters: [] }
    }

    function resetRuntime(): void {
      activeTab.value = 'all-creatives'
      closeCreative()
      closeAllCreatives()
    }

    function resetState(dropPreferences = false): void {
      resetRuntime()
      if (dropPreferences) preferences.value = {}
    }

    return {
      activeTab,
      preferences,
      sidebarCreative,
      allCreativesModal,
      preference,
      patchPreference,
      setTab,
      openCreative,
      closeCreative,
      openAllCreatives,
      closeAllCreatives,
      resetRuntime,
      resetState,
    }
  },
  { persist: { key: 'creativeAi:view', pick: ['preferences'] } },
)
